/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  getWidgetMessagePort,
  registerAsync,
  registerEvent,
  registerFunction,
  registerProperty,
} from '@b2chatorg/chat-center-widget-sdk/dist/internal';
import { reduxStoreReadable } from '@b2chatorg/chat-center-widget-sdk/dist/redux';
import type * as Types from '@b2chatorg/chat-center-widget-sdk/dist/types';
import { eventEmitter } from '@b2chatorg/chat-center-widget-sdk/dist/utils/eventEmitter';
import { writable } from '@b2chatorg/chat-center-widget-sdk/dist/utils/store';
import client, { APIClient } from '@client-sdk';
import {
  CHAT_CLOSED_BY_AGENT,
  CHAT_CLOSED_BY_CONTACT,
} from '@src/actions/agentconsole';
import { tagColor } from '@src/components/AdminHome/Tags/TagLabel';
import { ContactTag } from '@src/components/AdminHome/Tags/types';
import { MediaType, MessagingProvider } from '@src/model/frontendmodel';
import {
  ChatMessage,
  checkFile,
  queueChatMessage,
} from '@src/reducers/chatMessageQueue';
import {
  changeWrittenMessage,
  saveContactChat,
  updateChatOnHold,
} from '@src/reducers/chats';
import { fetchContactTagsById, fetchConverTags } from '@src/reducers/tags';
import { isChatComposerDisabled } from '@src/selectors/agentConsole';
import { Tag } from '@src/services/tags.service';
import store, { saga } from '@src/store/reduxstore';
import { ChatTray } from '@src/types/chats';
import { ContactInfo } from '@src/types/contact';
import env from '@src/utils/env';
import mediaUtils from '@src/utils/mediaUtils';
import { call, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

class WidgetStore {
  port = getWidgetMessagePort();

  constructor() {
    registerProperty(this.port, 'activeChat', this.activeChat);
    this.activeChat.subscribe(value =>
      this.onActiveChatChanged.dispatch(value)
    );
    registerProperty(this.port, 'activeContactInfo', this.activeContactInfo);
    registerProperty(this.port, 'agentInfo', this.agentInfo);
    registerProperty(this.port, 'departments', this.departments);
    registerProperty(
      this.port,
      'inputMessageContent',
      this.inputMessageContent
    );
    registerProperty(this.port, 'activeContactTags', this.activeContactTags);
    registerProperty(this.port, 'activeChatTags', this.activeChatTags);
    registerProperty(this.port, 'themeMode', this.themeMode);
    registerEvent(this.port, 'chatClosed', this.chatClosed);
    registerEvent(this.port, 'onThemeModeChange', this.onThemeModeChange);
    this.themeMode.subscribe(value => this.onThemeModeChange.dispatch(value));

    registerAsync(this.port, 'getChatTags', this.getChatTags);
    registerAsync(this.port, 'assignChatTag', this.assignChatTag);
    registerAsync(this.port, 'unassignChatTag', this.unassignChatTag);

    registerAsync(this.port, 'getContactTags', this.getContactTags);
    registerAsync(this.port, 'assignContactTag', this.assignContactTag);
    registerAsync(this.port, 'unassignContactTag', this.unassignContactTag);

    registerAsync(this.port, 'findChat', this.findChat);
    registerFunction(
      this.port,
      'setInputMessageContent',
      this.setInputMessageContent
    );
    registerFunction(this.port, 'updateChatInfo', this.updateChatInfo);
    registerFunction(
      this.port,
      'getContactInfoProperties',
      this.getContactInfoProperties
    );
    registerFunction(this.port, 'getUUID', this.getUUID);
    registerFunction(this.port, 'sendMessage', this.sendMessage);
    registerAsync(this.port, 'findContact', this.findContact);
    registerAsync(this.port, 'getJWTSecret', this.getJWTSecret);

    saga.run(this.task, this);
  }

  *task(self: WidgetStore) {
    yield takeLatest(CHAT_CLOSED_BY_AGENT, function* onChatClosedByAgent() {
      yield call(self.chatClosed.dispatch, 'closed-by-agent');
    });
    yield takeLatest(CHAT_CLOSED_BY_CONTACT, function* onChatClosedByContact() {
      yield call(self.chatClosed.dispatch, 'closed-by-contact');
    });
  }

  getUUID = () => uuid();

  getJWTSecret = async () => {
    const token = await APIClient.getJWT();
    if (!token) {
      throw new Error('Failed to retrieve JWT token');
    }
    return { token };
  };

  updateChatInfo = async (
    chatId: string,
    contactId: string,
    data: Partial<Types.ContactInfo>,
    signal: AbortSignal
  ) => {
    let contact;
    try {
      type Response = { contact: ContactInfo };
      const response =
        await client.resources.contacts.actions.getContactInfo<Response>({
          signal,
          params: { id: contactId },
        });

      if (response.error) return false;

      contact = response.data.contact;

      contact.fullName = data.fullName || contact.fullName;
      contact.identificationNumber =
        data.identificationNumber || contact.identificationNumber;
      contact.email = data.email || contact.email;
      contact.phoneNumber = data.phoneNumber || contact.phoneNumber;
      contact.mobileNumber = data.mobileNumber || contact.mobileNumber;
      contact.address = data.address || contact.address;
      contact.attributes.forEach(attr => {
        const nextValue = data.attributes?.find(
          ({ attrId }) => attrId === attr.attrId
        )?.value;

        attr.value = nextValue || attr.value;
      });
    } catch {
      throw new Error(`contactId '${contactId}' not found`);
    }

    try {
      type SaveContactResponse = { contact: ContactInfo };

      const response =
        await client.resources.contacts.actions.saveActiveContact<SaveContactResponse>(
          {
            signal,
            params: { contactId, activeChatId: chatId },
            data: contact,
          }
        );
      if (response.error) return false;
      const activeChat = this.activeChat.get();
      if (activeChat.contact.contactId === contactId) {
        this.activeContactInfo.set(contactInfoSummary(contact));
      }
      store.dispatch(
        updateChatOnHold({ id: chatId, fullName: contact.fullName })
      );

      const contactChat = store
        .getState()
        .chats.chatsOnHold.tray.find(
          item => `${item.contact.id}` === contactId
        );

      if (contactChat)
        store.dispatch(
          saveContactChat({
            chatId: contactChat.id,
            contact: { ...contactChat?.contact, fullName: contact.fullName },
          })
        );
      return true;
    } catch {
      throw new Error(`update fail`);
    }
  };

  getContactInfo = async (contactId: string, signal: AbortSignal) => {
    type Response = { contact: ContactInfo };
    const response =
      await client.resources.contacts.actions.getContactInfo<Response>({
        signal,
        params: { id: contactId },
      });
    if (response.error) throw response.error;
    return contactInfoSummary(response.data.contact);
  };

  getContactInfoProperties = async (signal: AbortSignal) => {
    type Response = Types.ContactInfoAttrProperties[];
    const standardBotId = `${store.getState().departments.items[0]?.botId}`;
    const response =
      await client.resources.contacts.actions.getContactAttributes<Response>({
        signal,
        params: { id: standardBotId },
      });
    if (response.error) {
      return [];
    }
    return response.data;
  };

  findContact = async (query: Types.FindContactQuery, signal: AbortSignal) => {
    try {
      type Response = {
        data: unknown[];
        total: number;
      };
      const response =
        await client.resources.contacts.actions.searchContacts<Response>({
          signal,
          data: {
            filter: {
              searchFilter: query.name,
              companyFilter: query.company || '',
              createdAtFrom: null,
              createdAtTo: null,
              onlyBlacklisted: false,
              searchByLastUpdate: false,
            },
            limit: query.limit,
            offset: query.offset,
          },
        });

      if (response.error) {
        throw response.error;
      }
      return {
        query,
        data: response.data.data,
        total: response.data.total,
      };
    } catch {
      return {
        query,
        data: [],
        total: 0,
      };
    }
  };

  sendMessage = async (message: Types.Message) => {
    const {
      data: [chat],
    } = await this.findChat({
      chatId: message.chatId,
      limit: 1,
      offset: 0,
    });

    if (!chat) {
      throw new Error(`chatId: '${message.chatId}' not found`);
    }

    if (
      chat.serviceWindow === 'EXPIRED' ||
      chat.status === 'CLOSED' ||
      chat.status === 'CLOSED_BY_CONTACT' ||
      chat.status === 'LEFT_BY_CONTACT'
    ) {
      throw new Error('chat not available');
    }

    if (message.type === 'text' || message.type === undefined) {
      store.dispatch(
        queueChatMessage(
          {
            chatId: message.chatId,
            messageId: uuid(),
            type: MediaType.TEXT,
            text: message.text,
          } as ChatMessage,
          message.chatId,
          MessagingProvider.WHATSAPP360
        )
      );
    } else {
      if (!message.file) {
        throw new Error(`file is required`);
      }

      checkFile(message.file, chat.provider as MessagingProvider);

      const mediaType = mediaUtils.mimeTypeToMediaType(message.file.type);

      store.dispatch(
        queueChatMessage(
          {
            messageId: uuid(),
            type: mediaType as MediaType,
            caption: message.caption,
            file: message.file,
          } as ChatMessage,
          message.chatId,
          chat.provider as MessagingProvider
        )
      );
    }
  };

  findChat = async (query: Types.FindChatQuery, signal?: AbortSignal) => {
    try {
      type Response = { filteredChats: ChatTray[]; totalChats: number };
      const response =
        await client.resources.agentConsole.actions.chats<Response>({
          signal,
          params: { limit: query.limit ?? 10, offset: query.offset ?? 0 },
          data: {
            type: 'ON_HOLD',
            contactName: query.contactName,
            chatId: query.chatId,
            windowLimit: false,
            conversationLimit: false,
            noAnswerFromAgent: query.unanswered,
            provider: [],
            viaBotAccountId: [],
            viaBusinessProcessNode: [],
            tags: [],
          },
        });

      if (response.error) {
        throw response.error;
      }

      const data = response.data.filteredChats.map(item => ({
        chatId: item.id,
        contact: {
          id: item.contact.id,
          fullName: item.contact.fullName,
          avatarUrl: item.contact.avatarUrl,
        },
        provider: item.provider,
        serviceWindow: item.hasPassedMaxTime ? 'EXPIRED' : 'ACTIVE',
        status: item.status,
        viewerUrl: this.getViewerUrl(item.id),
        tags: item.tags,
        accountMessaging: {
          account: item.viaBotAccount,
          alias: item.viaBotAccountAlias,
        },
        department: undefined,
        lastMessageReceivedAt: item.lastMsgReceivedAt,
        lastMessageSentAt: item.lastMsgSentAt ?? -1,
        startTimestamp: item.startTimestamp,
      }));

      return { data, query, total: response.data.totalChats };
    } catch {
      return { data: [], query, total: 0 };
    }
  };

  getContactTags = async (contactId: string) => {
    const [tagsR, assignedR] = await Promise.allSettled([
      client.resources.newTags.actions.getTags<ContactTag[]>({
        params: { type: 'CONTACT' },
      }),
      client.resources.newTags.actions.getTagsByContact<ContactTag[]>({
        params: { contactId },
      }),
    ]);

    if (tagsR.status === 'rejected' || tagsR.value.error) return [];

    const assigned =
      assignedR.status === 'rejected' || assignedR.value.error
        ? []
        : assignedR.value.data;

    return tagsR.value.data.reduce((tags, item) => {
      if (item.active) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        tags.push({
          id: `${item.tagId}`,
          name: item.name,
          color: tagColor(item.color),
          emoji: item.emoji || '',
          assigned: assigned.some(({ tagId }) => tagId === item.tagId),
        });
      }

      return tags;
    }, [] as (Types.Tag & { assigned: boolean })[]);
  };

  assignContactTag = async (contactId: string, tag: Types.Tag) => {
    const [response] = await Promise.allSettled([
      client.resources.newTags.actions.assignToContact({
        data: {
          contactId,
          tagIds: [+tag.id],
        },
      }),
    ]);

    if (response.status === 'rejected' || response.value.error) return false;

    if (contactId === this.activeContactInfo.get().contactId)
      store.dispatch(fetchContactTagsById({ contactId: +contactId }));

    return true;
  };

  unassignContactTag = async (contactId: string, tag: Types.Tag) => {
    const [response] = await Promise.allSettled([
      client.resources.newTags.actions.unassignToContact({
        params: { contactId, tagId: tag.id },
      }),
    ]);

    if (response.status === 'rejected' || response.value.error) return false;

    if (contactId === this.activeContactInfo.get().contactId)
      store.dispatch(fetchContactTagsById({ contactId: +contactId }));

    return true;
  };

  getChatTags = async (chatId: number) => {
    try {
      const response = await client.resources.tags.actions.getAll<Tag[]>({
        params: { chatId },
      });
      if (response.error) return [];

      return response.data.reduce<(Types.Tag & { assigned?: boolean })[]>(
        (tags, item) => {
          if (item.active) {
            tags.push({
              id: item.tagName,
              name: item.tagName,
              color: tagColor(item.color),
              emoji: item.emoji || '',
              assigned: item.assigned,
            });
          }
          return tags;
        },
        []
      );
    } catch {
      return [];
    }
  };

  assignChatTag = async (
    chatId: string,
    tag: Types.Tag,
    signal: AbortSignal
  ) => {
    try {
      const response = await client.resources.tags.actions.assign({
        signal,
        params: { chatId },
        data: { tagName: tag.id, emoji: tag.emoji, color: tag.color },
      });
      if (response.error) return false;

      if (chatId === this.activeChat.get().chatId) {
        const tags = await client.resources.tags.actions.getAll<Tag[]>({
          params: { chatId },
        });
        if (tags.data) store.dispatch(fetchConverTags(tags.data));
      }

      return true;
    } catch {
      return false;
    }
  };

  unassignChatTag = async (
    chatId: string,
    tag: Types.Tag,
    signal: AbortSignal
  ) => {
    try {
      const response = await client.resources.tags.actions.delete({
        signal,
        params: { chatId },
        data: { tagName: tag.id },
      });
      if (response.error) return false;
      if (chatId === this.activeChat.get().chatId) {
        const tags = await client.resources.tags.actions.getAll<Tag[]>({
          params: { chatId },
        });
        if (tags.data) store.dispatch(fetchConverTags(tags.data));
      }

      return true;
    } catch {
      return false;
    }
  };

  setInputMessageContent = (content: Types.InputMessageContent) => {
    store.dispatch(
      changeWrittenMessage({ chatId: content.chatId, message: content.text })
    );
  };

  inputMessageContent = reduxStoreReadable(
    store,
    state => {
      const chatId = state.chats.activeChat?.id;
      if (!chatId) return { chatId, text: '' };
      return { chatId, text: state.chats.writtenMessages[chatId] };
    },
    [state => state.chats.writtenMessages[state.chats.activeChat?.id || '']]
  );

  updateContactInfoFulfill = eventEmitter<boolean>();

  chatClosed = eventEmitter<string>();

  onActiveChatChanged = eventEmitter<Types.Chat>();

  onThemeModeChange = eventEmitter<'light' | 'dark'>();

  themeMode = writable<'light' | 'dark'>(
    (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') ||
      'light',
    set => {
      const observer = new MutationObserver(mutations => {
        const hasThemeChange = mutations.some(
          m => m.attributeName === 'data-theme'
        );
        if (hasThemeChange) {
          const mode = document.documentElement.getAttribute('data-theme') as
            | 'light'
            | 'dark';
          set(mode || 'light');
        }
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
      });
      return () => observer.disconnect();
    }
  );

  activeContactTags = reduxStoreReadable(
    store,
    state =>
      state.adminHome.tags.contactTags.reduce<
        (Types.Tag & { assigned: boolean })[]
      >((tags, item) => {
        if (item.active) {
          tags.push({
            id: `${item.tagId}`,
            name: item.name,
            color: tagColor(item.color),
            emoji: item.emoji || '',
            assigned: state.adminHome.tags.contactTagsById.some(
              ({ tagId }) => tagId === item.tagId
            ),
          });
        }
        return tags;
      }, []),
    [
      state => state.adminHome.tags.contactTags,
      state => state.adminHome.tags.contactTagsById,
    ]
  );

  activeChatTags = reduxStoreReadable(
    store,
    state =>
      state.adminHome.tags.converTags.reduce<
        (Types.Tag & { assigned: boolean })[]
      >((tags, item) => {
        if (item.active) {
          tags.push({
            id: `${item.tagName}`,
            name: item.tagName!,
            color: tagColor(item.color),
            emoji: item.emoji || '',
            assigned: !!item.assigned,
          });
        }
        return tags;
      }, []),
    [state => state.adminHome.tags.converTags]
  );

  agentInfo = reduxStoreReadable(
    store,
    state => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const agent = state.loginAuthentication?.success;

      if (!agent) return {};

      return {
        agentId: agent.id,
        username: agent.localUsername,
        fullName: agent.fullName,
        mobileNumber: agent.mobileNumber,
        email: agent.email,
        role: agent.role.toLowerCase(),
        avatarUrl: agent.avatarUrl,
        departments: agent.businessProcesses.map(
          (item: Record<string, string>) => ({
            departmentId: `${item.id}`,
            code: item.code,
            tagName: item.name,
            active: item.state === 'ACTIVE',
          })
        ),
      };
    },
    [state => state.loginAuthentication.success?.localUsername]
  );

  departments = reduxStoreReadable(
    store,
    (state): Types.Department[] =>
      state.departments.items.map(item => ({
        departmentId: `${item.departmentId ?? 'Standard'}`,
        code: item.departmentCode,
        tagName: item.name,
        active: item.state === 'ACTIVE',
      })),
    [state => state.departments.items]
  );

  getViewerUrl = (chatId?: string) =>
    chatId ? `${env.WEB_SERVICE_BASE_URI}/viewer/${chatId}` : '';

  activeContact = reduxStoreReadable(
    store,
    state => {
      const { activeChat } = state.chats;
      return {
        contactId: `${activeChat?.contact?.id || ''}`,
      };
    },
    [state => state.chats.activeChat?.contact]
  );

  activeChat = reduxStoreReadable(
    store,
    (state): Types.Chat => {
      const { activeChat } = state.chats;

      if (!activeChat)
        return {
          chatId: '',
          contact: {
            contactId: '',
            fullName: '',
            avatarUrl: '',
          },
          provider: 'unknown',
          serviceWindow: 'EXPIRED',
          status: 'CLOSED',
          viewerUrl: '',
          department: undefined,
          accountMessaging: {
            account: '',
            alias: '',
          },
          lastMessageReceivedAt: -1,
          lastMessageSentAt: -1,
          startTimestamp: -1,
        };

      const { businessProcessNode } = activeChat;

      return {
        chatId: activeChat?.id || '',
        contact: {
          contactId: activeChat?.contact.id.toString() || '',
          fullName: activeChat?.contact.fullName || '',
          avatarUrl: activeChat?.contact.avatarUrl || '',
        },
        provider:
          (activeChat?.provider as Types.MessagingProvider) || 'unknown',
        serviceWindow: isChatComposerDisabled(state) ? 'EXPIRED' : 'ACTIVE',
        status: activeChat.status as Types.Chat['status'],
        viewerUrl: this.getViewerUrl(activeChat?.id),
        department: businessProcessNode
          ? {
              departmentId: `${businessProcessNode.id}`,
              code: businessProcessNode.code,
              tagName: businessProcessNode.name,
            }
          : undefined,
        accountMessaging: {
          account: activeChat.viaBotAccount,
          alias: activeChat.viaBotAccountAlias,
        },
        lastMessageReceivedAt: activeChat.lastMsgReceivedAt,
        lastMessageSentAt: activeChat.lastMsgSentAt ?? -1,
        startTimestamp: activeChat.startTimestamp,
      };
    },
    [state => state.chats.activeChat?.id, state => state.chats.activeChat]
  );

  agentOnline = reduxStoreReadable(
    store,
    (state): Types.AgentOnlineStatus =>
      state.user.userState?.online ? 'available' : 'unavailable'
  );

  activeContactInfo = writable(contactInfoSummary({}), set => {
    let controller = new AbortController();

    const unsub = this.activeContact.subscribe(async contact => {
      controller.abort();
      controller = new AbortController();

      if (contact.contactId) {
        try {
          set(await this.getContactInfo(contact.contactId, controller.signal));
        } catch {
          set(contactInfoSummary({}));
        }
      }
    });

    return () => {
      unsub();
      controller.abort();
    };
  });
}

function contactInfoSummary(data: Partial<ContactInfo>): Types.ContactInfo {
  return {
    contactId: `${data.id}`,
    address: data.address || '',
    city: data.city || '',
    company: data.company || '',
    country: data.country?.name || '',
    email: data.email || '',
    fullName: data.fullName || '',
    identificationNumber: data.identificationNumber || '',
    mobileNumber: data.mobileNumber || '',
    phoneNumber: data.phoneNumber || '',
    province: data.province?.name || '',
    attributes: data.attributes || [],
  };
}

let instance: WidgetStore;
// eslint-disable-next-line import/prefer-default-export
export const getWidgetStore = () => {
  if (!instance) {
    instance = new WidgetStore();
  }

  return instance;
};
